import { useQuery } from '@tanstack/react-query';
import { getSupabaseClient } from '../utils/supabaseClient';
import { WORK_ORDER_STATUS } from '../constants/statusEnums';
import { CUSTOMER_ADDRESS_COLUMNS } from '../utils/customerAddress';
import { companyDateKey, companyWallClockToUtc, addDaysToDateKey } from '../utils/dateFormatters';
import { fetchExpenses, filterExpensesByEmployee } from './useExpensesPageData';

// ============== FETCH FUNCTIONS ==============

// Resolve employee ID from user ID
const resolveEmployeeId = async (userId) => {
  const supabase = getSupabaseClient();
  const { data } = await supabase
    .from('employees')
    .select('id')
    .eq('user_id', userId)
    .single();
  return data?.id || userId;
};

// 1) Weekly hours from timesheets
const fetchWeeklyHours = async (employeeId, startOfWeek, endOfWeek) => {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('employee_timesheets')
    .select('regular_hours,overtime_hours')
    .eq('employee_id', employeeId)
    .gte('date', startOfWeek)
    .lte('date', endOfWeek);
  
  if (error) return 0;
  if (!data) return 0;
  
  return data.reduce((sum, t) => 
    sum + Number(t.regular_hours || 0) + Number(t.overtime_hours || 0), 0);
};

// 2) Next job (upcoming)
const fetchNextJob = async (companyId, userId) => {
  const supabase = getSupabaseClient();
  // "Next job" = the job you are ON, else the next one coming up. It had no lower bound, so
  // with 36 never-started past-dated jobs assigned it showed one from 11 days ago (board
  // #4226). In progress wins; otherwise scheduled from the start of the company's today
  // (a job earlier today you have not started yet still counts as next).
  const base = () => supabase
    .from('work_orders')
    .select('id,title,scheduled_start,status,customer_id')
    .eq('company_id', companyId)
    .eq('assigned_to', userId);
  const { data: current, error: currentError } = await base()
    .eq('status', WORK_ORDER_STATUS.IN_PROGRESS)
    .order('scheduled_start', { ascending: false, nullsFirst: false })
    .limit(1);
  let data = current;
  let error = currentError;
  if (!error && (!data || data.length === 0)) {
    const startOfToday = companyWallClockToUtc(companyDateKey(new Date()), '00:00');
    ({ data, error } = await base()
      .eq('status', WORK_ORDER_STATUS.SCHEDULED)
      .gte('scheduled_start', startOfToday)
      .order('scheduled_start', { ascending: true })
      .limit(1));
  }

  if (error || !data || data.length === 0) return null;

  // Attach customer if exists
  const job = data[0];
  if (job.customer_id) {
    const { data: customer } = await supabase
      .from('customers')
      .select(`id,name,${CUSTOMER_ADDRESS_COLUMNS}`)
      .eq('id', job.customer_id)
      .single();
    if (customer) {
      job.customers = customer;
    }
  }
  
  return job;
};

// 3) Owed to me — #6057. Expenses have no approval step (P29), so the old
// `status = 'pending'` read was only the column default (132 of 144 live rows). This is the
// Expenses page's own "Owed" filter, fetched through the SAME call the page makes and narrowed
// to the signed-in user, so the tile and the list it opens are one predicate.
// null (not []) when the read is refused or fails (a Crew seat was refused outright until
// 20261021_tech_own_expenses; it now reads only its own rows) — so the
// tile shows a dash instead of a confident $0.
const fetchOwedToMe = async (companyId, userId) => {
  try {
    const { expenses } = await fetchExpenses(companyId, { reimbursableFilter: 'yes', limit: null, offset: 0 });
    return filterExpensesByEmployee(expenses, userId);
  } catch (_) {
    return null;
  }
};

// 4) Upcoming jobs
const fetchUpcomingJobs = async (companyId, userId) => {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('work_orders')
    .select('id,title,scheduled_start,scheduled_end,estimated_duration,status,customer_id')
    .eq('company_id', companyId)
    .eq('assigned_to', userId)
    // In progress now, or scheduled from today on — the same bound fetchNextJob uses (#4226).
    // With none, the card listed eleven February jobs as "upcoming" beside a Next Job tile that
    // correctly said None (audit 2026-10-17).
    .or(`status.eq.${WORK_ORDER_STATUS.IN_PROGRESS},and(status.eq.${WORK_ORDER_STATUS.SCHEDULED},scheduled_start.gte.${companyWallClockToUtc(companyDateKey(new Date()), '00:00')})`)
    .order('scheduled_start', { ascending: true })
    .limit(20);
  
  if (error || !data) return [];
  
  // Batch fetch customers
  const customerIds = [...new Set(data.map(wo => wo.customer_id).filter(Boolean))];
  let customerMap = {};
  if (customerIds.length > 0) {
    const { data: custData } = await supabase
      .from('customers')
      .select(`id,name,${CUSTOMER_ADDRESS_COLUMNS}`)
      .in('id', customerIds);
    if (custData) {
      custData.forEach(c => { customerMap[c.id] = c; });
    }
  }
  
  // Attach customers
  data.forEach(wo => {
    if (wo.customer_id && customerMap[wo.customer_id]) {
      wo.customers = customerMap[wo.customer_id];
    }
  });
  
  return data;
};

// 5) Today's assigned jobs
const fetchTodayJobs = async (companyId, userId) => {
  const supabase = getSupabaseClient();
  const now = new Date();
  const startOfDay = new Date(now);
  startOfDay.setHours(0, 0, 0, 0);
  const endOfDay = new Date(now);
  endOfDay.setHours(23, 59, 59, 999);

  const { data, error } = await supabase
    .from('work_orders')
    .select('id,title,scheduled_start,scheduled_end,estimated_duration,status,customer_id')
    .in('status', [WORK_ORDER_STATUS.SCHEDULED, WORK_ORDER_STATUS.IN_PROGRESS, WORK_ORDER_STATUS.COMPLETED])
    .eq('company_id', companyId)
    .eq('assigned_to', userId)
    .gte('scheduled_start', startOfDay.toISOString())
    .lte('scheduled_start', endOfDay.toISOString())
    .order('scheduled_start', { ascending: true });

  if (error || !data) return [];

  const customerIds = [...new Set(data.map(wo => wo.customer_id).filter(Boolean))];
  let customerMap = {};
  if (customerIds.length > 0) {
    const { data: custData } = await supabase
      .from('customers')
      .select(`id,name,${CUSTOMER_ADDRESS_COLUMNS}`)
      .in('id', customerIds);
    if (custData) {
      custData.forEach(c => { customerMap[c.id] = c; });
    }
  }

  data.forEach(wo => {
    if (wo.customer_id && customerMap[wo.customer_id]) {
      wo.customers = customerMap[wo.customer_id];
    }
  });

  return data;
};

// 6) Recent activity (work orders, quotes, payments) — scoped to the signed-in user like
// every other widget on My Dashboard (board #4831). Jobs: assigned_to. Quotes: created_by
// (FK → users, same id space as userId) or assigned_to. Payments: via
// payments.invoice_id → invoices.work_order_id → work_orders.assigned_to (payments has no FK
// to work_orders, so the invoice hop is the only embeddable path).
const fetchRecentActivity = async (companyId, userId) => {
  const supabase = getSupabaseClient();
  
  const [workOrderActivityData, quoteActivityData, paymentActivityData] = await Promise.all([
    supabase
      .from('work_orders')
      .select('id,title,status,updated_at,customer_id')
      .in('status', [WORK_ORDER_STATUS.SCHEDULED, WORK_ORDER_STATUS.COMPLETED])
      .eq('company_id', companyId)
      .eq('assigned_to', userId)
      .order('updated_at', { ascending: false })
      .limit(10),
    supabase
      .from('work_orders')
      .select('id,title,status,updated_at,customer_id')
      .in('status', [WORK_ORDER_STATUS.SENT, WORK_ORDER_STATUS.CHANGES_REQUESTED])
      .eq('company_id', companyId)
      .or(`created_by.eq.${userId},assigned_to.eq.${userId}`)
      .order('updated_at', { ascending: false })
      .limit(10),
    supabase
      .from('payments')
      .select('id,amount,status,updated_at,invoices!inner(work_orders!invoices_work_order_id_fkey!inner(assigned_to))')
      .eq('company_id', companyId)
      .eq('invoices.work_orders.assigned_to', userId)
      .in('status', ['completed', 'partially_refunded', 'refunded', 'disputed'])
      .order('updated_at', { ascending: false })
      .limit(10)
  ]);
  
  // Batch fetch customers
  const allWOs = [
    ...(workOrderActivityData.data || []),
    ...(quoteActivityData.data || [])
  ];
  const customerIds = [...new Set(allWOs.map(wo => wo.customer_id).filter(Boolean))];
  let customerMap = {};
  if (customerIds.length > 0) {
    const { data: custData } = await supabase
      .from('customers')
      .select(`id,name,${CUSTOMER_ADDRESS_COLUMNS}`)
      .in('id', customerIds);
    if (custData) {
      custData.forEach(c => { customerMap[c.id] = c; });
    }
  }
  
  // Attach customers
  const attachCustomer = (wo) => {
    if (wo.customer_id && customerMap[wo.customer_id]) {
      wo.customers = customerMap[wo.customer_id];
    }
    return wo;
  };
  (workOrderActivityData.data || []).forEach(attachCustomer);
  (quoteActivityData.data || []).forEach(attachCustomer);
  
  const activities = [];
  
  // Work order state changes
  if (workOrderActivityData.data) {
    workOrderActivityData.data.forEach(wo => {
      let message = '';
      if (wo.status === WORK_ORDER_STATUS.SCHEDULED) {
        message = `Work order scheduled — ${wo.title}`;
      } else if (wo.status === WORK_ORDER_STATUS.COMPLETED) {
        message = `Work order completed — ${wo.title}`;
      }
      if (message) {
        activities.push({
          id: `wo-${wo.id}`,
          entityId: wo.id,
          type: 'work_order',
          message,
          time: new Date(wo.updated_at),
          status: wo.status
        });
      }
    });
  }
  
  // Quote state changes
  if (quoteActivityData.data) {
    quoteActivityData.data.forEach(q => {
      let message = '';
      if (q.status === WORK_ORDER_STATUS.SENT) {
        message = `Quote sent — ${q.title}`;
      } else if (q.status === WORK_ORDER_STATUS.CHANGES_REQUESTED) {
        message = `Changes requested — ${q.title}`;
      }
      if (message) {
        activities.push({
          id: `quote-${q.id}`,
          entityId: q.id,
          type: 'quote',
          message,
          time: new Date(q.updated_at),
          status: q.status
        });
      }
    });
  }
  
  // Payment events
  if (paymentActivityData.data && paymentActivityData.data.length > 0) {
    paymentActivityData.data.forEach(p => {
      let message = '';
      if (p.status === 'completed') {
        message = `Payment received — $${Number(p.amount).toLocaleString()}`;
      } else if (p.status === 'failed') {
        message = `Payment failed — $${Number(p.amount).toLocaleString()}`;
      }
      if (message) {
        activities.push({
          id: `payment-${p.id}`,
          type: 'payment',
          message,
          time: new Date(p.updated_at),
          status: p.status
        });
      }
    });
  }
  
  // Sort by time and take most recent 10
  activities.sort((a, b) => b.time - a.time);
  return activities.slice(0, 10);
};

// ============== MAIN FETCH FUNCTION ==============
export const fetchMyDashboardData = async ({ companyId, userId }) => {
  if (!companyId || !userId) {
    throw new Error('Company ID and User ID are required');
  }
  
  // This week (Sunday–Saturday) on the COMPANY calendar, as 'YYYY-MM-DD' days: timesheet
  // `date` is a calendar column, so the bounds are calendar days too.
  const todayKey = companyDateKey();
  const startOfWeek = addDaysToDateKey(todayKey, -new Date(`${todayKey}T00:00:00Z`).getUTCDay());
  const endOfWeek = addDaysToDateKey(startOfWeek, 6);
  
  // Resolve employee ID
  const employeeId = await resolveEmployeeId(userId);
  
  // Run parallel fetches
  const [weeklyHours, nextJob, owedToMe, upcomingJobs, todayJobs, recentActivity] = await Promise.all([
    fetchWeeklyHours(employeeId, startOfWeek, endOfWeek),
    fetchNextJob(companyId, userId),
    fetchOwedToMe(companyId, userId),
    fetchUpcomingJobs(companyId, userId),
    fetchTodayJobs(companyId, userId),
    fetchRecentActivity(companyId, userId)
  ]);
  
  return {
    weeklyHours,
    nextJob,
    owedToMe,
    upcomingJobs,
    todayJobs,
    recentActivity,
    loading: false
  };
};

// ============== HOOK ==============
export const useMyDashboardData = (companyId, userId) => {
  return useQuery({
    queryKey: ['myDashboard', companyId, userId],
    queryFn: () => fetchMyDashboardData({ companyId, userId }),
    staleTime: 5 * 60 * 1000,
    enabled: !!companyId && !!userId,
  });
};
